import React from 'react';
import './NudosTextArea.scss';

/**
 * React functional component corresponding to the Nudos text area (Nudos long text)
 * @property {string} label - Required
 * @property {string} placeholder - Required
 * @property {string} currentValue - Required, the string with the current text, as it is being stored as state or part of a form.
 * @property {number} maxLength - Required, the maximum number of characters allowed for the field.
 * @property {React.Dispatch<React.SetStateAction<string>>} setCurrentValue - Required, a react state set function to update the stored input value on changing the textarea input.
 * @property {string} componentSize - Optional, one of the standard sizes for the components, corresponding to one of the following words: "medium" or "large". If no value is passed the component will have the width of its parent container.
 * @property {boolean} isDeactivated - Optional, boolean indicating if the field is deactivated. If a value is provided the field will behave as a read only field and styles will be accordingly.
 * @property {boolean} hidenCounter - Optional, boolean indicating if the counter for the number of characters should be hidden
 * @property {boolean} noResize - Optional, a boolean indicating if the text area can or cant be resized
 * @property {string} xPadding - Optional, a valid number for the x padding in pixels (eg: 10)
 * @property {string} yPadding - Optional,a valid number for the y padding in pixels (eg: 10)
 * @property {number} customFontSize - Optional, a valid number for the font size padding in pixels (eg: 10)
 * @property {number} textHeight - Optional, a valid number for the text area height padding in pixels (eg: 10)
 * @property {boolean} isFilled - Optional, boolean indicating if the field should be forced to show as filled regardless of the current value
 * @property {string} customLabelClassName - Optional, a valid class name for the label
 * @property {string} customCounterClassName - Optional, a valid class name for the counter
 */
const NudosTextArea = ({
  label,
  placeholder,
  currentValue,
  maxLength,
  setCurrentValue,
  componentSize,
  isDeactivated,
  hidenCounter,
  noResize,
  xPadding,
  yPadding,
  customFontSize,
  textHeight,
  customClassName,
  loading,
  onKeyPress,
  isFilled,
  customCounterClassName = '',
  customTextAreaClassName = '',
  customLabelClassName = ''
}: InudosTextArea) => {
  const errorStyles = currentValue.length > maxLength ? 'error' : '';
  const filledStyles = (currentValue && currentValue.length > 0 && !isDeactivated) || isFilled ? 'filled' : '';
  const deactivatedStyles = isDeactivated ? 'deactivated' : '';

  const handleChange = (event: React.FormEvent) => {
    const { value } = event.target as HTMLTextAreaElement;
    setCurrentValue(value);
  };

  return (
    <div
      className={`nudosTextArea ${
        customClassName ? customClassName : ''
      } ${errorStyles} ${filledStyles} ${deactivatedStyles} ${componentSize ? componentSize : ''} `}
    >
      {label && <label className={customLabelClassName}>{label}</label>}
      <textarea
        onKeyPress={onKeyPress}
        className={`${noResize ? 'noRezise' : ''} ${customTextAreaClassName}`}
        style={{
          height: textHeight ? `${textHeight}px` : '',
          fontSize: customFontSize ? `${customFontSize}px` : '',
          padding: `${yPadding ? `${yPadding}px` : ''} ${xPadding ? `${xPadding}px` : ''}`
        }}
        placeholder={placeholder}
        readOnly={isDeactivated}
        value={currentValue}
        onChange={handleChange}
        disabled={loading}
      />
      {!hidenCounter && (
        <div
          className={`lengthText ${customCounterClassName}`}
        >{`${currentValue?.length}/${maxLength}`}</div>
      )}
    </div>
  );
};

export default NudosTextArea;

export interface InudosTextArea {
  label?: string;
  placeholder: string;
  currentValue: string;
  maxLength: number;
  setCurrentValue: React.Dispatch<React.SetStateAction<string>>;
  componentSize?: string;
  isDeactivated?: boolean;
  hidenCounter?: boolean;
  noResize?: boolean;
  xPadding?: string;
  yPadding?: string;
  customFontSize?: number;
  textHeight?: number;
  customClassName?: string;
  customTextAreaClassName?: string;
  loading?: boolean;
  onKeyPress?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  isFilled?: boolean;
  customLabelClassName?: string;
  customCounterClassName?: string;
}
